import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../models/chart_model";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { formatDateForAPI, R_CHART_TYPES } from "../../Rajasthan/const";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import { useSelector } from "react-redux";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { AddDatesWithFileName } from "../../../helpers/helper";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { MetricChipHead } from "../../../components/Metrics";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";
import {
  getJvvnlDemand,
  getJvvnlDiscomPrediction,
  getJvvnlDiscomEnergy,
  getJvvnlFeeders,
  getJvvnlGssLoad,
  getJvvnlPredictionDateRange,
  getJvvnlSubstationPrediction,
  getJvvnlSubstations,
  getJvvnlWeatherByLocation,
} from "../../../Rest_api/RestApi";
import { getMonthStr } from "../Utility/demandHelpers";
import {
  JvvnlDiscomEnergyRow,
  JvvnlDemandRow,
  JvvnlFeeder,
  JvvnlGssLoadRow,
  JvvnlSubstation,
} from "../../../models/jvvnl";
import { SearchBox } from "../../../components/Search";

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================
const JVVNL_DEMAND_UNITS = ["KW", "KWh"] as const;
const JVVNL_AGG_UNITS = ["MW", "MWh"] as const;
const JVVNL_DEFAULT_DISCOM = "Jaipur";

const FUNCTIONS = ["Comparative", "Cumulative"] as const;
type FunctionMode = (typeof FUNCTIONS)[number];

const VARIABLES = ["Demand", "Weather", "Forecast"] as const;
const JVVNL_FORECAST_LABEL = "JVVNL";

const WEATHER_PARAMS = [
  {
    key: "temperature_2m",
    label: "Temperature (°C)",
    chartLabel: "🌡️ Temperature (°C)",
    unit: "°C",
  },
  {
    key: "relative_humidity_2m",
    label: "Humidity (%)",
    chartLabel: "💧 Humidity (%)",
    unit: "%",
  },
  {
    key: "apparent_temperature",
    label: "Apparent Temp (°C)",
    chartLabel: "🌡️ Apparent Temp (°C)",
    unit: "°C",
  },
  {
    key: "precipitation",
    label: "Precipitation (mm)",
    chartLabel: "🌧️ Precipitation (mm)",
    unit: "mm",
  },
  { key: "rain", label: "Rain (mm)", chartLabel: "🌧️ Rain (mm)", unit: "mm" },
  {
    key: "cloud_cover",
    label: "Cloud Cover (%)",
    chartLabel: "☁️ Cloud Cover (%)",
    unit: "%",
  },
  {
    key: "wind_speed_10m",
    label: "Wind 10m (km/h)",
    chartLabel: "💨 Wind 10m (km/h)",
    unit: "km/h",
  },
  {
    key: "sunshine_duration",
    label: "Sunshine Duration (s)",
    chartLabel: "☀️ Sunshine Duration (s)",
    unit: "s",
  },
  {
    key: "direct_radiation",
    label: "Direct Radiation (W/m²)",
    chartLabel: "🔆 Direct Radiation (W/m²)",
    unit: "W/m²",
  },
];

const Intervals = [
  { label: "Time Slot", key: "time", values: ["block", "date"], isTime: true },
  { label: "Date", key: "date", values: ["date"], isTime: false },
  { label: "Month", key: "month", values: ["month"], isTime: false },
];

const DEMAND_TAB_KEY = "Demand";
const LEGACY_DEMAND_TAB_KEY = "Feeder Demand";

const GROUP_BY_OPTIONS = [
  { label: "JVVNL Load", rowKey: "group_discom", fallback: "JVVNL" },
  {
    label: "Transco Substation",
    rowKey: "group_substation",
    fallback: "Transco Substation",
  },
  { label: "Feeder", rowKey: "group_feeder", fallback: "Feeder" },
] as const;

type GroupByOption = (typeof GROUP_BY_OPTIONS)[number];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
function pickFirstString(
  source: Record<string, unknown> | undefined,
  keys: string[],
): string | undefined {
  if (!source) return undefined;
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return undefined;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export function JvvnlFeederDemand() {
  // --- Redux State ---
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const effectiveTabKey = useMemo(() => {
    if (DefaultDates?.[DEMAND_TAB_KEY]) return DEMAND_TAB_KEY;
    if (DefaultDates?.[LEGACY_DEMAND_TAB_KEY]) return LEGACY_DEMAND_TAB_KEY;
    if (activeTab === LEGACY_DEMAND_TAB_KEY) return DEMAND_TAB_KEY;
    return activeTab;
  }, [DefaultDates, activeTab]);

  const currentRangeDate = DefaultDates?.[effectiveTabKey]?.defaultRangeDate;

  // --- Local State ---
  const [selectedUnit, setSelectedUnit] = useState<string>(
    JVVNL_DEMAND_UNITS[0],
  );
  const [selectedGroupBy, setSelectedGroupBy] = useState<GroupByOption>(
    GROUP_BY_OPTIONS[0],
  );
  const [selectedBlock, setSelectedBlock] = useState<{ value: number }[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [selectedFunction, setSelectedFunction] =
    useState<FunctionMode>("Comparative");
  const [selectedVariables, setSelectedVariables] = useState<string[]>([
    "Demand",
  ]);
  const [selectedWeatherParams, setSelectedWeatherParams] = useState<string[]>([
    "temperature_2m",
  ]);

  // Metadata State
  const [feeders, setFeeders] = useState<JvvnlFeeder[]>([]);
  const [feedersLoaded, setFeedersLoaded] = useState(false);
  const [substations, setSubstations] = useState<JvvnlSubstation[]>([]);
  const [substationsLoaded, setSubstationsLoaded] = useState(false);

  // Filter Options State
  const [selectedFeederOptions, setSelectedFeederOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [selectedSubstationOptions, setSelectedSubstationOptions] = useState<
    { value: number; label: string }[]
  >([]);

  // Data State
  const [rawData, setRawData] = useState<JvvnlDemandRow[]>([]);
  const [gssLoadData, setGssLoadData] = useState<JvvnlGssLoadRow[]>([]);
  const [discomData, setDiscomData] = useState<JvvnlDiscomEnergyRow[]>([]);
  const [weatherData, setWeatherData] = useState<any[]>([]);
  const [forecastRows, setForecastRows] = useState<any[]>([]);

  // Chart State
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // ============================================================================
  // 1. METADATA LOADING (Substations & Feeders)
  // ============================================================================
  useEffect(() => {
    if (
      selectedGroupBy.rowKey !== "group_substation" ||
      substationsLoaded ||
      substations.length > 0
    )
      return;
    let cancelled = false;
    getJvvnlSubstations()
      .then((list) => {
        if (!cancelled) {
          const filteredList = list.filter((s) =>
            ["BANSUR", "BEHROR", "KOTPUTLI", "NIMRANA", "MANOHARPUR"].includes(
              s.station,
            ),
          );
          setSubstations(filteredList);
        }
      })
      .catch(() => {
        if (!cancelled) setSubstations([]);
      })
      .finally(() => {
        if (!cancelled) setSubstationsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedGroupBy.rowKey, substations.length, substationsLoaded]);

  useEffect(() => {
    if (
      selectedGroupBy.rowKey !== "group_feeder" ||
      feedersLoaded ||
      feeders.length > 0
    )
      return;
    let cancelled = false;
    getJvvnlFeeders()
      .then((list) => {
        if (!cancelled) setFeeders(list);
      })
      .catch((e) => {
        console.error("Failed to load JVVNL feeders", e);
        if (!cancelled) setFeeders([]);
      })
      .finally(() => {
        if (!cancelled) setFeedersLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [feeders.length, feedersLoaded, selectedGroupBy.rowKey]);

  // ============================================================================
  // 2. MEMOS & SELECTIONS
  // ============================================================================
  const feederLocation = useMemo(
    () => new Map(feeders.map((f) => [f.id, f.location])),
    [feeders],
  );
  const feederMetaById = useMemo(
    () => new Map(feeders.map((f) => [f.id, f as any])),
    [feeders],
  );
  const substationNameByFeederId = useMemo(
    () =>
      new Map(
        feeders.map((f) => [
          f.id,
          pickFirstString(f as any, [
            "substation_name",
            "substation",
            "location_name",
          ]) || "Unknown Substation",
        ]),
      ),
    [feeders],
  );
  const substationById = useMemo(
    () => new Map(substations.map((s) => [s.id, s])),
    [substations],
  );

  const feederOptions = useMemo(
    () => feeders.map((f) => ({ value: f.id, label: f.name })),
    [feeders],
  );
  const substationOptions = useMemo(
    () => substations.map((s) => ({ value: s.id, label: s.station })),
    [substations],
  );

  // Adjust selected options when full lists load
  useEffect(() => {
    setSelectedFeederOptions((prev) => {
      const filtered = prev.filter((o) =>
        feederOptions.some((f) => f.value === o.value),
      );
      return filtered.length === 0 && feederOptions.length > 0
        ? feederOptions.slice(0, 2)
        : filtered;
    });
  }, [feederOptions]);

  useEffect(() => {
    setSelectedSubstationOptions((prev) => {
      const filtered = prev.filter((o) =>
        substationOptions.some((s) => s.value === o.value),
      );
      return filtered.length === 0 && substationOptions.length > 0
        ? [substationOptions[0]]
        : filtered;
    });
  }, [substationOptions]);

  // Adjust units based on group by
  useEffect(() => {
    if (selectedGroupBy.rowKey === "group_feeder") {
      if (!JVVNL_DEMAND_UNITS.includes(selectedUnit as any))
        setSelectedUnit(JVVNL_DEMAND_UNITS[0]);
    } else {
      if (!JVVNL_AGG_UNITS.includes(selectedUnit as any))
        setSelectedUnit(JVVNL_AGG_UNITS[0]);
    }
  }, [selectedGroupBy.rowKey, selectedUnit]);

  // Extract selected IDs
  const selectedFeederIds = useMemo(
    () => selectedFeederOptions.map((o) => o.value),
    [selectedFeederOptions],
  );
  const selectedSubstationIds = useMemo(
    () => selectedSubstationOptions.map((o) => o.value),
    [selectedSubstationOptions],
  );
  const selectedSubstationNames = useMemo(
    () =>
      selectedSubstationIds
        .map((id) => substationById.get(id)?.station)
        .filter(Boolean) as string[],
    [selectedSubstationIds, substationById],
  );

  const effectiveFeederIds = useMemo(() => {
    if (selectedGroupBy.rowKey === "group_feeder") return selectedFeederIds;
    if (selectedGroupBy.rowKey === "group_substation") {
      if (selectedSubstationNames.length === 0) return [];
      return feeders
        .filter((f) =>
          selectedSubstationNames.includes(
            substationNameByFeederId.get(f.id) || "Unknown Substation",
          ),
        )
        .map((f) => f.id);
    }
    return [];
  }, [
    feeders,
    selectedFeederIds,
    selectedGroupBy.rowKey,
    selectedSubstationNames,
    substationNameByFeederId,
  ]);

  const selectedWeatherParam =
    selectedWeatherParams[0] || WEATHER_PARAMS[0].key;
  const selectedWeatherParamMeta = useMemo(
    () =>
      WEATHER_PARAMS.find((w) => w.key === selectedWeatherParam) ||
      WEATHER_PARAMS[0],
    [selectedWeatherParam],
  );
  const selectedWeatherMetricLabel =
    selectedWeatherParamMeta.chartLabel ||
    selectedWeatherParamMeta.label ||
    selectedWeatherParam;

  const weatherSeries = useMemo(() => {
    if (!selectedVariables.includes("Weather")) return [];
    if (selectedGroupBy.rowKey === "group_feeder") return selectedFeederOptions;
    if (selectedGroupBy.rowKey === "group_substation")
      return selectedSubstationOptions;
    return [{ value: 1, label: "JVVNL" }];
  }, [
    selectedGroupBy.rowKey,
    selectedVariables,
    selectedFeederOptions,
    selectedSubstationOptions,
  ]);

  const forecastSeries = useMemo(() => {
    if (selectedGroupBy.rowKey !== "group_discom")
      return selectedSubstationOptions;
    const discomSeries = new Map<number, { value: number; label: string }>();
    for (const row of forecastRows) {
      if (!discomSeries.has(row.substationId)) {
        discomSeries.set(row.substationId, {
          value: row.substationId,
          label: row.substationName || JVVNL_FORECAST_LABEL,
        });
      }
    }
    return discomSeries.size === 0
      ? [{ value: 1, label: JVVNL_FORECAST_LABEL }]
      : Array.from(discomSeries.values());
  }, [forecastRows, selectedGroupBy.rowKey, selectedSubstationOptions]);

  // ============================================================================
  // 3. DATA FETCHING (Demand, GSS Load, Discom Energy)
  // ============================================================================
  // Demand Data
  useEffect(() => {
    if (
      selectedGroupBy.rowKey === "group_discom" ||
      selectedGroupBy.rowKey === "group_substation"
    )
      return;
    if (!currentRangeDate) {
      setLoading(false);
      return;
    }
    if (!feedersLoaded) return;
    if (feeders.length === 0) {
      setRawData([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getJvvnlDemand({
      startDate: formatDateForAPI(currentRangeDate.startDate),
      endDate: formatDateForAPI(currentRangeDate.endDate),
      feederIds: selectedFeederIds.length > 0 ? selectedFeederIds : undefined,
      feederLocation,
    })
      .then(({ rows }) => {
        if (!cancelled) setRawData(rows);
      })
      .catch((err) => {
        console.error("Error fetching JVVNL demand:", err);
        if (!cancelled) setRawData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    currentRangeDate,
    feedersLoaded,
    feeders.length,
    feederLocation,
    selectedFeederIds,
    selectedGroupBy.rowKey,
  ]);

  // GSS Load Data
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_substation") {
      setGssLoadData([]);
      return;
    }
    if (!currentRangeDate || selectedSubstationIds.length === 0) {
      setGssLoadData([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    const startDateStr = formatDateForAPI(currentRangeDate.startDate);
    const endDateStr = formatDateForAPI(currentRangeDate.endDate);

    Promise.all(
      selectedSubstationIds.map((id) =>
        getJvvnlGssLoad({
          startDate: startDateStr,
          endDate: endDateStr,
          stationId: id,
        }),
      ),
    )
      .then((results) => {
        if (!cancelled) setGssLoadData(results.flatMap((r) => r.rows));
      })
      .catch((err) => {
        console.error("Error fetching GSS load:", err);
        if (!cancelled) setGssLoadData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentRangeDate, selectedGroupBy.rowKey, selectedSubstationIds]);

  // Discom Energy Data
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_discom") {
      setDiscomData([]);
      return;
    }
    if (!currentRangeDate) {
      setDiscomData([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getJvvnlDiscomEnergy({
      startDate: formatDateForAPI(currentRangeDate.startDate),
      endDate: formatDateForAPI(currentRangeDate.endDate),
    })
      .then((rows) => {
        if (!cancelled) setDiscomData(rows);
      })
      .catch((err) => {
        console.error("Error fetching JVVNL discom data:", err);
        if (!cancelled) setDiscomData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentRangeDate, selectedGroupBy.rowKey]);

  // ============================================================================
  // 4. WEATHER & FORECAST FETCHING
  // ============================================================================
  // Weather
  useEffect(() => {
    if (
      !["group_discom", "group_substation", "group_feeder"].includes(
        selectedGroupBy.rowKey,
      ) ||
      !currentRangeDate ||
      !selectedVariables.includes("Weather")
    ) {
      setWeatherData([]);
      return;
    }

    let cancelled = false;
    const startDateStr = formatDateForAPI(currentRangeDate.startDate);
    const endDateStr = formatDateForAPI(currentRangeDate.endDate);

    const weatherTargets =
      selectedGroupBy.rowKey === "group_feeder"
        ? selectedFeederOptions
            .map((o) => {
              const loc = feederLocation.get(o.value);
              return loc == null
                ? null
                : { seriesId: o.value, seriesLabel: o.label, locationId: loc };
            })
            .filter(
              (
                v,
              ): v is {
                seriesId: number;
                seriesLabel: string;
                locationId: number;
              } => v !== null,
            )
        : selectedGroupBy.rowKey === "group_substation"
          ? selectedSubstationOptions.map((o) => ({
              seriesId: o.value,
              seriesLabel: o.label,
              locationId: substationById.get(o.value)?.location_id ?? o.value,
            }))
          : [{ seriesId: 1, seriesLabel: "JVVNL", locationId: 1 }];

    if (weatherTargets.length === 0) {
      setWeatherData([]);
      return;
    }

    (async () => {
      try {
        const uniqueLocations = new Map<
          number,
          { seriesId: number; seriesLabel: string }[]
        >();
        for (const target of weatherTargets) {
          const existing = uniqueLocations.get(target.locationId) || [];
          existing.push({
            seriesId: target.seriesId,
            seriesLabel: target.seriesLabel,
          });
          uniqueLocations.set(target.locationId, existing);
        }

        const allWeatherData = (
          await Promise.all(
            Array.from(uniqueLocations.entries()).map(
              async ([locationId, seriesTargets]) => {
                const rows = await getJvvnlWeatherByLocation({
                  startDate: startDateStr,
                  endDate: endDateStr,
                  locationId,
                });
                return seriesTargets.flatMap((target) =>
                  rows.map((row) => ({
                    ...row,
                    seriesId: target.seriesId,
                    seriesLabel: target.seriesLabel,
                  })),
                );
              },
            ),
          )
        ).flat();

        if (!cancelled) setWeatherData(allWeatherData);
      } catch (err) {
        console.error("Error fetching weather data:", err);
        if (!cancelled) setWeatherData([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    currentRangeDate,
    selectedGroupBy.rowKey,
    selectedFeederOptions,
    selectedSubstationOptions,
    substationById,
    feederLocation,
    selectedVariables,
  ]);

  // Forecast
  useEffect(() => {
    const isDiscomMode = selectedGroupBy.rowKey === "group_discom";
    if (
      !selectedVariables.includes("Forecast") ||
      !currentRangeDate ||
      (!isDiscomMode && selectedSubstationIds.length === 0)
    ) {
      setForecastRows([]);
      return;
    }

    let cancelled = false;
    const startDate = formatDateForAPI(currentRangeDate.startDate);
    const endDate = formatDateForAPI(currentRangeDate.endDate);
    const selectedSubstationId = selectedSubstationIds[0] ?? 1;
    const discomName =
      substationById.get(selectedSubstationId)?.discom?.trim() ||
      JVVNL_DEFAULT_DISCOM;

    (async () => {
      try {
        if (isDiscomMode) {
          const { modelId } = await getJvvnlPredictionDateRange({
            substationId: selectedSubstationId,
            startDate,
            endDate,
            discom: discomName,
          });
          const { predictions } = await getJvvnlDiscomPrediction({
            discom: discomName,
            startDate,
            endDate,
            modelId: modelId ?? 1,
          });
          if (cancelled) return;
          setForecastRows(
            predictions.map((p) => ({
              substationId: 1,
              substationName: JVVNL_FORECAST_LABEL,
              date: p.date,
              block: p.slot,
              month: getMonthStr(p.date),
              forecast_mw: p.predicted_mw,
            })),
          );
          return;
        }

        const rowsBySubstation = await Promise.all(
          selectedSubstationIds.map(async (substationId) => {
            const { modelId } = await getJvvnlPredictionDateRange({
              substationId,
              startDate,
              endDate,
            });
            const { predictions } = await getJvvnlSubstationPrediction({
              substationId,
              startDate,
              endDate,
              modelId: modelId ?? 3,
            });
            const substationName =
              substationById.get(substationId)?.station ||
              `Substation ${substationId}`;
            return predictions.map((p) => ({
              substationId,
              substationName,
              date: p.date,
              block: p.slot,
              month: getMonthStr(p.date),
              forecast_mw: p.predicted_mw,
            }));
          }),
        );
        if (cancelled) return;
        setForecastRows(rowsBySubstation.flat());
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching JVVNL substation forecast:", error);
        setForecastRows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    currentRangeDate,
    selectedGroupBy.rowKey,
    selectedSubstationIds,
    selectedVariables,
    substationById,
  ]);

  // ============================================================================
  // 5. CHART DATA FORMATTING & LEGENDS
  // ============================================================================
  const activeDataKey = useMemo(() => {
    if (selectedGroupBy.rowKey === "group_discom")
      return selectedUnit === "MWh" ? "drawal_mwh" : "drawal_mw";
    if (selectedGroupBy.rowKey === "group_substation")
      return selectedUnit === "MWh" ? "value_mwh" : "value_mw";
    return selectedUnit === "KW"
      ? "demand_kw"
      : selectedUnit === "KWh"
        ? "demand_kwh"
        : selectedUnit === "MW"
          ? "demand_mw"
          : "demand_mwh";
  }, [selectedGroupBy.rowKey, selectedUnit]);

  const convertForecastMwToUnit = useCallback(
    (valueMw: number) => {
      if (selectedUnit === "KW") return valueMw * 1000;
      if (selectedUnit === "KWh") return (valueMw * 1000) / 2;
      if (selectedUnit === "MWh") return valueMw / 2;
      return valueMw;
    },
    [selectedUnit],
  );

  const forecastBySeriesXKey = useMemo(() => {
    const grouped = new Map<string, number[]>();
    for (const row of forecastRows) {
      const xKey =
        selectedInterval.key === "time"
          ? `${row.date}_${row.block}`
          : selectedInterval.key === "date"
            ? row.date
            : row.month;
      const key = `${row.substationId}__${xKey}`;
      const values = grouped.get(key) || [];
      values.push(convertForecastMwToUnit(row.forecast_mw));
      grouped.set(key, values);
    }
    const averaged = new Map<string, number>();
    grouped.forEach((values, key) =>
      averaged.set(key, values.reduce((sum, v) => sum + v, 0) / values.length),
    );
    return averaged;
  }, [forecastRows, selectedInterval.key, convertForecastMwToUnit]);

  const appendForecastToRows = useCallback(
    (rows: any[]) => {
      if (!selectedVariables.includes("Forecast")) return rows;
      return rows.map((row) => {
        const xKey =
          selectedInterval.key === "time"
            ? `${row.date}_${row.block}`
            : selectedInterval.key === "date"
              ? row.date
              : row.month;
        const forecastCols = forecastSeries.reduce<
          Record<string, number | null>
        >((acc, series) => {
          acc[`forecast_${series.value}`] =
            forecastBySeriesXKey.get(`${series.value}__${xKey}`) ?? null;
          return acc;
        }, {});
        return { ...row, ...forecastCols };
      });
    },
    [
      selectedVariables,
      selectedInterval.key,
      forecastSeries,
      forecastBySeriesXKey,
    ],
  );

  const enrichRowsWithWeather = useCallback(
    (rows: any[], getXKey: (row: any) => string) => {
      if (
        !selectedVariables.includes("Weather") ||
        weatherData.length === 0 ||
        weatherSeries.length === 0
      )
        return rows;

      const weatherBySeriesXKey = new Map<string, number[]>();
      for (const row of weatherData) {
        const rawValue = (row as any)[selectedWeatherParam];
        if (rawValue == null || isNaN(parseFloat(rawValue))) continue;
        const xKey = getXKey({
          ...row,
          block: row.block ?? row.time_block,
          month: row.month ?? (row.date ? getMonthStr(row.date) : ""),
        });
        const key = `${row.seriesId}__${xKey}`;
        const values = weatherBySeriesXKey.get(key) || [];
        values.push(parseFloat(rawValue));
        weatherBySeriesXKey.set(key, values);
      }

      return rows.map((row) => {
        const xKey = getXKey(row);
        const weatherCols: Record<string, number | null> = {};
        for (const series of weatherSeries) {
          const values = weatherBySeriesXKey.get(`${series.value}__${xKey}`);
          weatherCols[`weather_${selectedWeatherParam}_${series.value}`] =
            values && values.length > 0
              ? values.reduce((a, b) => a + b, 0) / values.length
              : null;
        }
        return { ...row, ...weatherCols };
      });
    },
    [selectedVariables, weatherData, weatherSeries, selectedWeatherParam],
  );

  const legends = useMemo(() => {
    const out: any[] = [];
    if (selectedVariables.includes("Demand")) {
      chartKeys.forEach((key, idx) => {
        out.push({
          dataKey: `${key}_${activeDataKey}`,
          name: `${normalizeDisplayLabelWithoutUndefined(key)}`,
          stroke: ExchangeColors[idx % ExchangeColors.length],
          type: selectedChartType.value as ChartType,
          stackId:
            selectedChartType.stackId !== undefined ? "demand" : undefined,
        });
      });
    }
    if (selectedVariables.includes("Weather")) {
      weatherSeries.forEach((series, index) => {
        out.push({
          dataKey: `weather_${selectedWeatherParam}_${series.value}`,
          name: `${selectedWeatherMetricLabel} - ${series.label}`,
          unit: selectedWeatherParamMeta.unit || "",
          stroke: lightenColor(
            ExchangeColors[(index + 4) % ExchangeColors.length],
            10,
          ),
          type: ChartType.Line as ChartType,
          yAxisId: "right" as const,
          strokeDasharray: "4 4",
        });
      });
    }
    if (selectedVariables.includes("Forecast")) {
      forecastSeries.forEach((substation, index) => {
        out.push({
          dataKey: `forecast_${substation.value}`,
          name: `${substation.label} (Forecast)`,
          stroke: lightenColor(
            ExchangeColors[(index + 7) % ExchangeColors.length],
            5,
          ),
          type: ChartType.Line as ChartType,
          strokeDasharray: "6 3",
        });
      });
    }
    return out;
  }, [
    selectedVariables,
    selectedWeatherParam,
    selectedWeatherParamMeta,
    chartKeys,
    selectedChartType,
    activeDataKey,
    forecastSeries,
    weatherSeries,
    selectedWeatherMetricLabel,
  ]);

  // Main Chart Data Generation
  useEffect(() => {
    let processedRows: any[] = [];
    let aggregationKeys: string[] = [];
    let averageKeys: string[] = [];

    const blockSet =
      selectedBlock.length > 0
        ? new Set(selectedBlock.map((b) => b.value))
        : null;

    if (selectedGroupBy.rowKey === "group_discom") {
      if (discomData.length === 0) {
        setChartData([]);
        setChartKeys([]);
        setTableData([]);
        return;
      }
      const rows = blockSet
        ? discomData.filter((r) => blockSet.has(r.block))
        : discomData;
      processedRows = rows.map((r) => ({
        ...r,
        drawal_mwh: r.drawal_mw / 2,
        month: getMonthStr(r.date),
        series_group: selectedFunction === "Cumulative" ? "Total" : "JVVNL",
      }));
      aggregationKeys = ["drawal_mw", "drawal_mwh"];
      averageKeys = ["drawal_mw"];
    } else if (selectedGroupBy.rowKey === "group_substation") {
      if (gssLoadData.length === 0) {
        setChartData([]);
        setChartKeys([]);
        setTableData([]);
        return;
      }
      const rows = blockSet
        ? gssLoadData.filter((r) => blockSet.has(r.block))
        : gssLoadData;
      processedRows = rows.map((r) => ({
        ...r,
        value_mwh: r.value_mw / 2,
        month: getMonthStr(r.date),
        series_group:
          selectedFunction === "Cumulative" ? "Total" : r.station_name,
      }));
      aggregationKeys = ["value_mw", "value_mwh"];
      averageKeys = ["value_mw"];
    } else {
      if (rawData.length === 0) {
        setChartData([]);
        setChartKeys([]);
        setTableData([]);
        return;
      }
      const rows = blockSet
        ? rawData.filter((r) => blockSet.has(r.block))
        : rawData;
      const filtered = rows.filter(
        (item) =>
          effectiveFeederIds.length === 0 ||
          effectiveFeederIds.includes(item.feeder),
      );
      processedRows = filtered.map((item) => {
        const rowWithExtras = item as unknown as Record<string, unknown>;
        const feederMeta = feederMetaById.get(item.feeder);
        const groupValue =
          pickFirstString(rowWithExtras, [
            "substation_name",
            "substation",
            "location_name",
          ]) ||
          pickFirstString(feederMeta, [
            "substation_name",
            "substation",
            "location_name",
          ]) ||
          item.feeder_name ||
          "Unknown";
        return {
          ...item,
          demand_mw: item.demand_kw / 1000,
          demand_mwh: item.demand_kwh / 1000,
          month: getMonthStr(item.date),
          series_group:
            selectedFunction === "Cumulative" ? "Total" : groupValue,
        };
      });
      aggregationKeys = ["demand_kw", "demand_kwh", "demand_mw", "demand_mwh"];
      averageKeys = ["demand_kw", "demand_mw"];
    }

    const keys =
      selectedInterval.key === "time"
        ? ["date", "block"]
        : selectedInterval.key === "date"
          ? ["date"]
          : [selectedInterval.values[0]];

    const cData = FormatChartData({
      keys,
      tableKeys: ["date", "block", "month"],
      groupBy: ["series_group"],
      volumeKey: "",
      aggregationKeys,
      averageKeys,
      averageDuplicationKeys: ["date", "block"],
      priceKey: "",
      data: processedRows,
    });

    const enriched = enrichRowsWithWeather(cData.data || [], (row: any) =>
      selectedInterval.key === "time"
        ? `${row.date}_${row.block}`
        : selectedInterval.key === "date"
          ? row.date
          : row.month,
    );

    setChartData(appendForecastToRows(enriched));
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [
    rawData,
    gssLoadData,
    discomData,
    selectedGroupBy.rowKey,
    selectedBlock,
    selectedFunction,
    selectedInterval,
    effectiveFeederIds,
    feederMetaById,
    enrichRowsWithWeather,
    appendForecastToRows,
  ]);

  const metrics = useMemo(() => {
    if (rawData.length === 0) return null;
    const filteredRaw =
      selectedBlock.length === 0
        ? rawData
        : rawData.filter((r) =>
            selectedBlock.map((b) => b.value).includes(r.block),
          );
    if (filteredRaw.length === 0) return null;

    const slotTotals = new Map<string, number>();
    for (const row of filteredRaw) {
      const slotKey = `${row.date}_${row.block}`;
      slotTotals.set(slotKey, (slotTotals.get(slotKey) || 0) + row.demand_kw);
    }

    const aggregatedKw = Array.from(slotTotals.values());
    if (aggregatedKw.length === 0) return null;

    const peak = Math.max(...aggregatedKw);
    const min = Math.min(...aggregatedKw);
    const sum = aggregatedKw.reduce((acc, value) => acc + value, 0);
    const avg = sum / aggregatedKw.length;

    return {
      peak: { value: Number(peak.toFixed(2)), unit: "KW" },
      avg: { value: Number(avg.toFixed(2)), unit: "KW" },
      min: { value: Number(min.toFixed(2)), unit: "KW" },
      loadFactor: {
        value: Number((peak > 0 ? (avg / peak) * 100 : 0).toFixed(2)),
        unit: "%",
      },
      total: { value: Number((sum / 2 / 1000).toFixed(2)), unit: "MWh" },
    };
  }, [rawData, selectedBlock]);

  // ============================================================================
  // 6. RENDER
  // ============================================================================
  const xDataKey =
    selectedInterval.key === "time" ? "block" : selectedInterval.key;
  const secondDataKey = () => (selectedInterval.isTime ? "date" : undefined);
  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: selectedInterval.isTime
      ? ["block", selectedInterval.values[1]]
      : [selectedInterval.values[0]],
    extraLabels: selectedInterval.values.map((v) =>
      v === "date" ? "Date" : v,
    ),
    filename: AddDatesWithFileName({
      fileName: "JVVNL Demand",
      dates: currentRangeDate,
    }),
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Intervals"
            items={Intervals.map((it) => it.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(_, i) => setSelectedInterval(Intervals[i])}
          />
          <FilterMenu
            title="Unit"
            items={
              selectedGroupBy.rowKey === "group_feeder"
                ? [...JVVNL_DEMAND_UNITS]
                : [...JVVNL_AGG_UNITS]
            }
            selectedItems={[selectedUnit]}
            onSelect={setSelectedUnit}
          />
          <FilterMenu
            title="Function"
            items={[...FUNCTIONS]}
            selectedItems={[selectedFunction]}
            onSelect={(_, i) => setSelectedFunction(FUNCTIONS[i])}
          />
          <FilterMenu
            title="Group By"
            sortAlphabetically={false}
            items={GROUP_BY_OPTIONS.map((i) => i.label)}
            selectedItems={[selectedGroupBy.label]}
            onSelect={(_, i) => setSelectedGroupBy(GROUP_BY_OPTIONS[i])}
          />
          <FilterMenu
            title="Variable"
            items={[...VARIABLES]}
            selectedItems={selectedVariables}
            setSelectedItems={setSelectedVariables}
          />
          <FilterMenu
            title="Weather Metric"
            items={WEATHER_PARAMS.map((w) => ({
              value: w.key,
              label: w.label,
            }))}
            selectedItems={[selectedWeatherParam]}
            onSelect={(i) => setSelectedWeatherParams([i])}
            disabled={!selectedVariables.includes("Weather")}
          />
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((it) => it.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(_, i) => setSelectedChartType(R_CHART_TYPES[i])}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              title="Demand"
              items={[
                {
                  label: "Peak Load",
                  value: metrics.peak.value,
                  unit: metrics.peak.unit,
                },
                {
                  label: "Average Load",
                  value: metrics.avg.value,
                  unit: metrics.avg.unit,
                },
              ]}
            />
            <MetricChipHead
              title="Energy"
              items={[
                {
                  label: "Min Load",
                  value: metrics.min.value,
                  unit: metrics.min.unit,
                },
                {
                  label: "Total Energy",
                  value: metrics.total.value,
                  unit: metrics.total.unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader />
          ) : (
            <>
              <div className="flex flex-col gap-y-2 justify-between border-gray-200 bg-white">
                <div className="gas-heading gap-x-10">
                  <h2 className="chartHeading w-60">Overview</h2>
                  <div className="flex gap-x-6 justify-end items-center">
                    {selectedGroupBy.rowKey === "group_feeder" && (
                      <SearchBox
                        options={feederOptions}
                        isMany
                        placeholder="Search Feeder"
                        selectedOptions={selectedFeederOptions}
                        onChange={(v: any) => setSelectedFeederOptions(v || [])}
                      />
                    )}
                    {(selectedGroupBy.rowKey === "group_substation" ||
                      (selectedVariables.includes("Forecast") &&
                        selectedGroupBy.rowKey !== "group_discom")) && (
                      <SearchBox
                        options={substationOptions}
                        isMany
                        placeholder="Search Substation"
                        selectedOptions={selectedSubstationOptions}
                        onChange={(v: any) =>
                          setSelectedSubstationOptions(v || [])
                        }
                      />
                    )}
                    <BlockRangeSelector
                      blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                      placeholder="Search Block"
                      selectedOptions={selectedBlock.map((it) => it.value)}
                      setSelectedOptions={setSelectedBlock as any}
                    />
                    <button
                      onClick={() =>
                        [
                          downloadChartTableCSV,
                          downloadSummaryCSV,
                          downloadChartTableCSV,
                        ][
                          ["Chart", "Summary", "Table"].indexOf(dataTab.label)
                        ]?.(tablePar)
                      }
                      className="downloadButton"
                    >
                      Download Data
                    </button>
                  </div>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  key={`jvvnl-${selectedUnit}-${selectedInterval.key}-${selectedChartType.value}-${selectedFunction}`}
                  data={chartData}
                  showBrush={chartData.length > 96}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedInterval.isTime}
                  onlyTitle
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  secondYAxisLabel={
                    selectedVariables.includes("Weather")
                      ? selectedWeatherMetricLabel
                      : undefined
                  }
                  legends={legends}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey={xDataKey}
                  xTick={
                    selectedInterval.isTime && selectedBlock.length === 0
                      ? (v: any) => renderHourTick(v, 10)
                      : undefined
                  }
                  secondXDataKey={secondDataKey()}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip note={<p>JVVNL Demand + Weather</p>} text="">
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
